import { Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import CategoryGrid from './components/CategoryGrid';
import SeoArticlesSection from './components/SeoArticlesSection';
import TrustSection from './components/TrustSection';
import DeepSeoSection from './components/DeepSeoSection';
import CtaSection from './components/CtaSection';
import FooterSection from './components/FooterSection';

const comparisonData = [
  { tool: 'GTBuy Spreadsheet', automation: 5, speed: 'Fast', bestFor: 'Scaling Users' },
  { tool: 'Kakobuy Spreadsheet', automation: 3, speed: 'Medium', bestFor: 'Budget Users' },
  { tool: 'Superbuy Spreadsheet', automation: 4, speed: 'Fast', bestFor: 'Advanced Users' },
];

const faqs = [
  {
    question: 'What is GTBuy Spreadsheet?',
    answer:
      'GTBuy Spreadsheet is a powerful automation and comparison system designed specifically for buying agents and sourcing professionals. It helps you compare spreadsheet tools, manage product sourcing workflows, and optimize order tracking with real-time data organization features.',
  },
  {
    question: 'How does GTBuy Spreadsheet compare to Kakobuy Spreadsheet?',
    answer:
      'GTBuy Spreadsheet offers faster data handling and more flexible integration options compared to Kakobuy Spreadsheet. While Kakobuy is a solid choice for budget-conscious users, GTBuy excels in scalability and automation features, making it the preferred option for growing sourcing operations.',
  },
  {
    question: 'Is GTBuy Spreadsheet safe to use?',
    answer:
      'Yes, GTBuy Spreadsheet is built with transparent workflow systems and user-controlled spreadsheet structures. There are no hidden automation processes, and all data organization follows industry-standard practices for security and reliability.',
  },
  {
    question: 'What product categories are available on GTBuy?',
    answer:
      'GTBuy Spreadsheet supports 14 product categories including Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear/Underpants, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category links directly to the main platform for seamless browsing.',
  },
  {
    question: 'Can I use GTBuy Spreadsheet for high-volume sourcing operations?',
    answer:
      'Absolutely. GTBuy Spreadsheet is specifically designed for high-volume sourcing operations. Its automation system handles bulk data organization, cross-platform spreadsheet sync, and e-commerce order tracking efficiently, making it ideal for professional buying agents managing large product catalogs.',
  },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="inline-flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`text-xs ${i < rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-background-300'}`}
        ></i>
      ))}
    </span>
  );
}

export default function Home() {
  const siteUrl = 'https://gtbuyspreadsheet.fit';

  return (
    <main className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'GTBuy Spreadsheet',
            url: siteUrl,
            description:
              'The #1 comparison and automation system for buying agents. Compare spreadsheet tools, find the best automation workflow, and optimize your sourcing operations.',
            potentialAction: {
              '@type': 'SearchAction',
              target: `${siteUrl}/search?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'GTBuy Spreadsheet',
            url: siteUrl,
            logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg',
            description:
              'The #1 comparison and automation system for buying agents. Helping sourcing professionals make informed decisions with real feature breakdowns, pricing insights, and workflow automation analysis.',
            sameAs: ['https://kaspreadsheet.com'],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'What is GTBuy Spreadsheet?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'GTBuy Spreadsheet is a powerful automation and comparison system designed specifically for buying agents and sourcing professionals. It helps you compare spreadsheet tools, manage product sourcing workflows, and optimize order tracking with real-time data organization features.',
                },
              },
              {
                '@type': 'Question',
                name: 'How does GTBuy Spreadsheet compare to Kakobuy Spreadsheet?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'GTBuy Spreadsheet offers faster data handling and more flexible integration options compared to Kakobuy Spreadsheet. While Kakobuy is a solid choice for budget-conscious users, GTBuy excels in scalability and automation features, making it the preferred option for growing sourcing operations.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is GTBuy Spreadsheet safe to use?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes, GTBuy Spreadsheet is built with transparent workflow systems and user-controlled spreadsheet structures. There are no hidden automation processes, and all data organization follows industry-standard practices for security and reliability.',
                },
              },
              {
                '@type': 'Question',
                name: 'What product categories are available on GTBuy?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'GTBuy Spreadsheet supports 14 product categories including Shoes, Hoodies/Sweaters, T-Shirts, Jackets, Pants/Shorts, Headwear, Sets, Underwear/Underpants, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each category links directly to the main platform for seamless browsing.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can I use GTBuy Spreadsheet for high-volume sourcing operations?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. GTBuy Spreadsheet is specifically designed for high-volume sourcing operations. Its automation system handles bulk data organization, cross-platform spreadsheet sync, and e-commerce order tracking efficiently, making it ideal for professional buying agents managing large product catalogs.',
                },
              },
            ],
          }),
        }}
      />

      <Navbar />
      <HeroSection />

      <section className="bg-background-50 py-12 md:py-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              Instant Comparison: GTBuy vs Kakobuy vs Superbuy
            </h3>
            <div className="overflow-x-auto rounded-2xl border border-background-200/70">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100">
                    <th className="px-5 py-3.5 text-xs font-semibold text-foreground-700 whitespace-nowrap">
                      Tool
                    </th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-foreground-700 whitespace-nowrap">
                      Automation
                    </th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-foreground-700 whitespace-nowrap">
                      Speed
                    </th>
                    <th className="px-5 py-3.5 text-xs font-semibold text-foreground-700 whitespace-nowrap">
                      Best For
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, idx) => (
                    <tr
                      key={row.tool}
                      className={`border-b border-background-200/50 last:border-0 ${
                        idx === 0 ? 'bg-accent-50' : ''
                      }`}
                    >
                      <td className="px-5 py-4">
                        <span
                          className={`text-sm font-semibold whitespace-nowrap ${idx === 0 ? 'text-foreground-950' : 'text-foreground-700'}`}
                        >
                          {row.tool}
                          {idx === 0 && (
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-accent-500 text-foreground-950 text-[10px] font-bold">
                              BEST
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <StarRating rating={row.automation} />
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-foreground-700 whitespace-nowrap">
                          {row.speed}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-foreground-600 whitespace-nowrap">
                          {row.bestFor}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-center text-xs text-foreground-400 mt-4">
              Based on independent feature analysis and user feedback. Updated July 2026.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-2">
              Why GTBuy Spreadsheet Wins
            </h3>
            <p className="text-foreground-500 text-sm text-center mb-10 max-w-xl mx-auto">
              Built for professionals who demand the best in spreadsheet automation
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  icon: 'ri-flashlight-line',
                  title: 'Built for Scalable Automation',
                  desc: 'Handles growing data volumes without performance drops. Designed for high-volume sourcing operations.',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Reduces Manual Tracking Errors',
                  desc: 'Structured workflows eliminate common data entry mistakes and streamline order management.',
                },
                {
                  icon: 'ri-speed-up-line',
                  title: 'Faster Data Handling',
                  desc: 'Processes and organizes spreadsheet data faster than Kakobuy Spreadsheet in real-world tests.',
                },
                {
                  icon: 'ri-plug-line',
                  title: 'Flexible Integration Options',
                  desc: 'More adaptable than Superbuy Spreadsheet when connecting with external platforms and tools.',
                },
                {
                  icon: 'ri-stack-line',
                  title: 'Bulk Data Organization',
                  desc: 'Efficiently manages large catalogs across 14 product categories with intuitive sorting.',
                },
                {
                  icon: 'ri-global-line',
                  title: 'Cross-Platform Sync',
                  desc: 'Seamlessly synchronizes spreadsheet data across multiple devices and platforms in real time.',
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="bg-background-50 rounded-2xl p-5 md:p-6 hover:bg-background-50/80 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-lg text-accent-700`}></i>
                  </div>
                  <h4 className="font-semibold text-foreground-950 text-sm mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CategoryGrid />
      <SeoArticlesSection />
      <TrustSection />
      <DeepSeoSection />

      <section className="bg-background-100 py-16 md:py-20">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 text-center mb-10">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {faqs.map((faq) => (
                <details
                  key={faq.question}
                  className="group bg-background-50 rounded-2xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 md:px-6 py-4 cursor-pointer list-none">
                    <span className="text-sm font-semibold text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background-200 flex items-center justify-center group-open:rotate-45 transition-transform duration-200">
                      <i className="ri-add-line text-xs text-foreground-600"></i>
                    </span>
                  </summary>
                  <div className="px-5 md:px-6 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaSection />

      <section className="bg-background-50 py-12 md:py-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              Popular Use Cases for GTBuy Spreadsheet
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                'e-commerce order tracking',
                'product sourcing management',
                'buying agent workflow automation',
                'cross-platform spreadsheet sync',
                'bulk data organization systems',
                'inventory management automation',
                'supplier data coordination',
                'price comparison tracking',
              ].map((usecase) => (
                <a
                  key={usecase}
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-secondary-100 hover:text-foreground-950 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {usecase}
                </a>
              ))}
            </div>
            <p className="text-center text-xs text-foreground-400 mt-6">
              Click any use case to explore GTBuy Spreadsheet solutions on the main platform
            </p>
          </div>
        </div>
      </section>

      <FooterSection />
    </main>
  );
}